import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Play, Plus, CheckCircle2, XCircle, Ban, Circle, Loader2 } from 'lucide-react';

const CreateTestRunDialog = ({ projectId, onSuccess, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a test run name');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/projects/${projectId}/testruns`, { 
        name: name.trim(),
        description: description.trim() || null
      });
      toast.success('Test run created successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test run');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create Test Run</DialogTitle>
        <DialogDescription>
          Create a new test run to execute test cases. All current test cases will be included.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        <div>
          <Label htmlFor="name">Test Run Name *</Label>
          <Input 
            id="name" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            placeholder="e.g., Sprint 1 - Release 1.0" 
            required 
            data-testid="testrun-name-input" 
          />
        </div>
        <div>
          <Label htmlFor="description">Description (Optional)</Label>
          <Textarea 
            id="description" 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Add notes about this test run..."
            rows={3}
            data-testid="testrun-description-input" 
          />
        </div>
        <div className="flex gap-2 pt-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose} 
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading} 
            className="flex-1 bg-blueprint hover:bg-blueprint/90" 
            data-testid="submit-testrun-button"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</> : 'Create Test Run'}
          </Button>
        </div>
      </form>
    </>
  );
};

const TestRunCard = ({ testRun, onClick }) => {
  const passRate = testRun.pass_percentage || 0;
  
  const getStatusBadge = () => {
    if (testRun.status === 'completed') {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 ring-1 ring-green-600/20">
          Completed
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-600/20">
        In Progress
      </span>
    );
  };

  return (
    <Card 
      className="p-6 hover:border-azure/50 transition-all cursor-pointer" 
      onClick={onClick}
      data-testid={`testrun-card-${testRun.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-heading font-semibold text-blueprint mb-1">{testRun.name}</h3>
          {testRun.description && (
            <p className="text-sm text-muted-foreground mb-2">{testRun.description}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Created {new Date(testRun.created_at).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
            })}
          </p>
        </div>
        {getStatusBadge()}
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-3 pt-4 border-t border-border">
        <div className="text-center">
          <div className="text-lg font-mono font-bold text-blueprint">{testRun.total_cases || 0}</div>
          <div className="text-xs text-muted-foreground uppercase">Total</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-lg font-mono font-bold text-green-700">{testRun.passed || 0}</span>
          </div>
          <div className="text-xs text-muted-foreground uppercase">Passed</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <XCircle className="h-4 w-4 text-red-600" />
            <span className="text-lg font-mono font-bold text-red-700">{testRun.failed || 0}</span>
          </div>
          <div className="text-xs text-muted-foreground uppercase">Failed</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <Ban className="h-4 w-4 text-yellow-600" />
            <span className="text-lg font-mono font-bold text-yellow-700">{testRun.blocked || 0}</span>
          </div>
          <div className="text-xs text-muted-foreground uppercase">Blocked</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <Circle className="h-4 w-4 text-muted-foreground" />
            <span className="text-lg font-mono font-bold text-muted-foreground">{testRun.not_run || 0}</span>
          </div>
          <div className="text-xs text-muted-foreground uppercase">Not Run</div>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium text-blueprint">{passRate}% pass rate</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full flex">
            <div 
              className="bg-green-500 transition-all duration-300" 
              style={{ width: `${testRun.total_cases ? (testRun.passed / testRun.total_cases * 100) : 0}%` }} 
            />
            <div 
              className="bg-red-500 transition-all duration-300" 
              style={{ width: `${testRun.total_cases ? (testRun.failed / testRun.total_cases * 100) : 0}%` }} 
            />
            <div 
              className="bg-yellow-500 transition-all duration-300" 
              style={{ width: `${testRun.total_cases ? (testRun.blocked / testRun.total_cases * 100) : 0}%` }} 
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export const TestRunsTab = ({ projectId, testRuns, testCases, onUpdate }) => {
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const hasTestCases = testCases && testCases.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-blueprint">Test Runs</h2>
        <Button 
          className="gap-2 bg-blueprint hover:bg-blueprint/90" 
          onClick={() => setShowCreateDialog(true)} 
          disabled={!hasTestCases}
          data-testid="create-testrun-button"
        >
          <Plus className="h-4 w-4" />New Test Run
        </Button>
      </div>

      {!hasTestCases && (
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
          <p className="text-amber-800 text-sm">
            Generate test cases first before creating a test run. Go to the Test Cases tab and click "Generate QA Pack".
          </p>
        </Card>
      )}

      {testRuns.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="testruns-empty-state">
          <Play className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-heading font-semibold mb-2 text-blueprint">No test runs yet</h3>
          <p className="text-muted-foreground mb-6">
            {hasTestCases 
              ? 'Create a test run to start executing test cases' 
              : 'Generate test cases first, then create a test run'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {testRuns.map((tr) => (
            <TestRunCard 
              key={tr.id} 
              testRun={tr} 
              onClick={() => navigate(`/testruns/${tr.id}`)} 
            />
          ))}
        </div>
      )}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <CreateTestRunDialog 
            projectId={projectId} 
            onSuccess={onUpdate} 
            onClose={() => setShowCreateDialog(false)} 
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};